import { useTranslation } from 'react-i18next'
import { Card, CardBody, CardHeader } from '@structural-climbing/ui'
import { formatCurrency, TrendSpan } from './trend'
import type { ExternalProviderStats } from './types'

interface Props {
  stats: ExternalProviderStats
  currency: string
  periodLabel: string
  comparePeriodLabel: string
  comparePartial: boolean
}

/**
 * One partner that pays the gym per visit (ESX, 7card...). Its income is not
 * part of the revenue card, which counts only what was sold at the desk or
 * online; this block is where it shows.
 */
export function ExternalProviderCard({
  stats,
  currency,
  periodLabel,
  comparePeriodLabel,
  comparePartial,
}: Props) {
  const { t } = useTranslation()

  return (
    <Card>
      <CardHeader className="flex items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">{stats.provider}</h2>
        <p className="text-xs text-foreground/50">{periodLabel}</p>
      </CardHeader>
      <CardBody className="gap-3 pt-0">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <p className="text-xs text-foreground/50">{t('statistics.externalIncome')}</p>
            <p className="text-lg font-bold tabular-nums">
              {formatCurrency(stats.incomeA, currency)}
            </p>
            <TrendSpan
              current={stats.incomeA}
              previous={stats.incomeB}
              compareLabel={comparePeriodLabel}
              partial={comparePartial}
              formatDelta={(value) => formatCurrency(value, currency)}
            />
          </div>
          <div>
            <p className="text-xs text-foreground/50">{t('statistics.externalVisits')}</p>
            <p className="text-lg font-bold tabular-nums">{stats.visitsA}</p>
            <TrendSpan
              current={stats.visitsA}
              previous={stats.visitsB}
              compareLabel={comparePeriodLabel}
              partial={comparePartial}
            />
          </div>
          <div>
            <p className="text-xs text-foreground/50">{t('statistics.externalMembers')}</p>
            <p className="text-lg font-bold tabular-nums">{stats.activeMembers}</p>
          </div>
        </div>
        <p className="text-xs text-foreground/50">
          {t('statistics.externalDesc', { provider: stats.provider })}
        </p>
      </CardBody>
    </Card>
  )
}
